// context/CallContext.jsx
// ─────────────────────────────────────────────────────────────
// App-wide call foundation (C1). Mounted ONCE in App.js (inside auth +
// router). Owns: presence socket (via useCallNotifications), the incoming
// card, the outgoing/busy status card, the single call modal, and the
// recording-consent notice — so calls are made/received on every
// authenticated page.
//
// Call-state model (one CallLog row per call, keyed by room_id — backend
// advances it): invite→ringing, accept→answered, decline→declined,
// cancel/30s-timeout→missed, close→duration.
//
// This layer (C2) adds the notification UI states: outgoing ringing card
// with offline / no-answer / declined / busy variants, quick replies, and
// "ping me when free".
// ─────────────────────────────────────────────────────────────

import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { AuthContext } from "./AuthContext";
import { useCallNotifications } from "../hooks/useCallNotifications";
import IncomingCallToast from "../components/chat/IncomingCallToast";
import CallOutgoingCard from "../components/chat/CallOutgoingCard";
import VideoCallModal from "../components/chat/VideoCallModal";
import { toast } from "../components/ui/Toast";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const RING_TIMEOUT_MS = 30000;

// Same gate as KiraGate — no call chrome on auth pages or for superadmin.
const HIDDEN_PATHS = ["/login", "/welcome", "/accept-invite", "/forgot-password", "/reset-password", "/demo/"];

const noop = () => {};
const CallContext = createContext({
  startCall: noop, joinRoom: noop, acceptCall: noop, acceptAudio: noop,
  declineCall: noop, quickReply: noop, closeCall: noop, callOpen: false,
});

export const useCallContext = () => useContext(CallContext);

export function CallProvider({ children }) {
  const auth     = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const onHidden = HIDDEN_PATHS.some((p) => location.pathname.startsWith(p));
  const active =
    !auth?.loading &&
    auth?.isAuthenticated &&
    !!auth?.user &&
    auth.user.role !== "superadmin" &&
    !onHidden;

  const [incomingCall,    setIncomingCall]    = useState(null);
  const [outgoing,        setOutgoing]        = useState(null); // { status, targetUser, audioOnly, room_id?, replyText? }
  const [callConfig,      setCallConfig]      = useState(null);
  const [callOpen,        setCallOpen]        = useState(false);
  const [recordingNotice, setRecordingNotice] = useState(null);

  // Refs mirror state so the long-lived callbacks read the latest values.
  const incomingRef    = useRef(null);
  const outgoingRef    = useRef(null);
  const callOpenRef    = useRef(false);
  const currentRoomRef = useRef(null);   // { room_id, target_user_id?, caller_name? }
  const missedTimerRef = useRef(null);
  useEffect(() => { incomingRef.current = incomingCall; }, [incomingCall]);
  useEffect(() => { outgoingRef.current = outgoing; }, [outgoing]);
  useEffect(() => { callOpenRef.current = callOpen; }, [callOpen]);

  const setIncoming = useCallback((v) => { incomingRef.current = v; setIncomingCall(v); }, []);
  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });
  const clearMissedTimer = () => { if (missedTimerRef.current) { clearTimeout(missedTimerRef.current); missedTimerRef.current = null; } };

  const postCancel = (room) => {
    if (!room?.room_id) return;
    fetch(`${API_BASE}/api/calling/cancel`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({ room_id: room.room_id, target_user_id: room.target_user_id || "", caller_name: room.caller_name || "" }),
    }).catch(() => {});
  };

  // ── Call ended (any path) → record duration, free the busy slot (/close), drop modal ──
  const closeCall = useCallback((durationSecs) => {
    const room = currentRoomRef.current?.room_id;
    clearMissedTimer();
    if (room) {
      fetch(`${API_BASE}/api/calling/close`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ room_id: room, duration_secs: String(durationSecs || 0) }),
      }).catch(() => {});
    }
    setCallOpen(false);
    setCallConfig(null);
    currentRoomRef.current = null;
  }, []);

  // ── Caller pressed Cancel → mark ringing row missed, close, clear the card ──
  const cancelCall = useCallback(() => {
    postCancel(currentRoomRef.current);
    closeCall(0);
    setOutgoing(null);
  }, [closeCall]);

  // ── 30s with no answer → mark missed, close modal, leave a "No answer" card ──
  const handleNoAnswer = useCallback(() => {
    postCancel(currentRoomRef.current);
    closeCall(0);
    const o = outgoingRef.current;
    setOutgoing(o ? { status: "no_answer", targetUser: o.targetUser, audioOnly: o.audioOnly } : null);
  }, [closeCall]);

  // ── Start a call (caller): room → invite → ring, with busy/offline/missed ──
  const startCall = useCallback(async ({ targetUser, audioOnly = false, meeting_id = null, meeting_title = null } = {}) => {
    if (!targetUser?.id) return;
    const me = auth?.user;
    const myName = me?.full_name || me?.name || "Someone";
    try {
      const roomRes = await fetch(`${API_BASE}/api/calling/room`, { method: "POST", headers: authHeaders() });
      const { room_id } = await roomRes.json();
      const room = { room_id, target_user_id: String(targetUser.id), caller_name: myName };

      const inviteRes = await fetch(`${API_BASE}/api/calling/invite`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          caller_id: String(me?.id || ""), caller_name: myName,
          target_user_id: String(targetUser.id), room_id,
          audio_only: audioOnly, participants: [myName],
          receiver_name: targetUser.full_name || targetUser.name || "",
          meeting_id, meeting_title,
        }),
      });
      const data = await inviteRes.json().catch(() => ({}));

      // Busy — callee already on a call: show the busy card, don't ring.
      if (data?.busy) { setOutgoing({ status: "busy", targetUser, audioOnly }); return; }

      // Offline — invite not delivered: mark the row missed, offer to message.
      if (data?.delivered === false) {
        postCancel(room);
        setOutgoing({ status: "offline", targetUser, audioOnly, room_id });
        return;
      }

      // Ringing — open the modal (caller joins the room to detect the answer)
      // and show the outgoing ringing card until the callee joins.
      currentRoomRef.current = room;
      setOutgoing({ status: "ringing", targetUser, audioOnly, room_id });
      setCallConfig({ room_id, targetUser, audioOnly });
      setCallOpen(true);
      clearMissedTimer();
      missedTimerRef.current = setTimeout(handleNoAnswer, RING_TIMEOUT_MS);
    } catch (e) {
      console.error("startCall failed:", e);
    }
  }, [auth, handleNoAnswer]);

  // ── Join an existing room (KweryAI meeting join — direct join, NO ring/invite) ──
  const joinRoom = useCallback(({ room_id, targetUser = null, audioOnly = false, meeting_id = null, meeting_title = null } = {}) => {
    if (!room_id) return;
    currentRoomRef.current = { room_id };
    setCallConfig({ room_id, targetUser, audioOnly, meeting_id, meeting_title });
    setCallOpen(true);
  }, []);

  // ── Callee accepted (audioOnly=true → joins camera-off via VideoCallModal's video flag) ──
  const acceptCall = useCallback((roomId, audioOnly) => {
    setIncoming(null);
    if (roomId) {
      fetch(`${API_BASE}/api/calling/accept`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ room_id: roomId }),
      }).catch(() => {});
    }
    currentRoomRef.current = { room_id: roomId };
    setCallConfig({ room_id: roomId, targetUser: null, audioOnly: !!audioOnly });
    setCallOpen(true);
  }, [setIncoming]);
  const acceptAudio = useCallback((roomId) => acceptCall(roomId, true), [acceptCall]);

  // ── Callee declined / quick-reply → notify caller + end as declined ──
  const respond = useCallback((kind, text) => {
    const c = incomingRef.current;
    setIncoming(null);
    if (c?.room_id) {
      fetch(`${API_BASE}/api/calling/respond`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ room_id: c.room_id, kind, text: text || "" }),
      }).catch(() => {});
    }
  }, [setIncoming]);
  const declineCall = useCallback(() => respond("declined"), [respond]);
  const quickReply  = useCallback((text) => respond("quick_reply", text), [respond]);

  // ── Call connected (2nd participant joined) → drop the ringing card, keep the modal ──
  const onAnswered = useCallback(() => { clearMissedTimer(); setOutgoing(null); }, []);

  // ── Caller side: callee declined → show a brief "Declined" card, then auto-dismiss ──
  const showDeclined = useCallback((text) => {
    closeCall(0);
    const o = outgoingRef.current;
    setOutgoing({ status: "declined", targetUser: o?.targetUser, audioOnly: o?.audioOnly, replyText: text || null });
    setTimeout(() => setOutgoing((cur) => (cur?.status === "declined" ? null : cur)), 5000);
  }, [closeCall]);

  // ── Outgoing-card actions ──
  const dismissOutgoing = useCallback(() => setOutgoing(null), []);
  const retryOutgoing = useCallback(() => {
    const o = outgoingRef.current;
    setOutgoing(null);
    if (o?.targetUser) startCall({ targetUser: o.targetUser, audioOnly: o.audioOnly });
  }, [startCall]);
  const leaveMessage = useCallback(() => {
    setOutgoing(null);
    navigate("/chat");   // opens the messenger; peer preselect is a follow-up
  }, [navigate]);
  const pingWhenFree = useCallback(() => {
    const o = outgoingRef.current;
    const name = o?.targetUser?.full_name || o?.targetUser?.name || "them";
    if (o?.targetUser?.id) {
      fetch(`${API_BASE}/api/calling/ping-when-free`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ target_user_id: String(o.targetUser.id) }),
      }).catch(() => {});
    }
    setOutgoing(null);
    toast?.info?.(`We'll let you know when ${name} is free`);
  }, []);

  // ── Presence events (fresh closures each render — hook reads them via ref) ──
  useCallNotifications({
    enabled: active,
    onCallAccepted: (msg) => {
      // Explicit accept push from the callee (mirrors call_declined). Clears the
      // caller's ringing card and cancels the 30s no-answer timer the moment they
      // pick up — even if the first remote LiveKit track is slow to arrive.
      // onAnswered (first remote track) does the same; whichever lands first wins
      // and both are idempotent. Guarded to the current room so a stale/other-room
      // event can't wipe an unrelated card.
      const room = currentRoomRef.current?.room_id;
      if (msg?.room_id && room && String(msg.room_id) !== String(room)) return;
      clearMissedTimer();
      setOutgoing((o) => (o?.status === "ringing" ? null : o));
    },
    onIncomingCall: (msg) => {
      if (callOpenRef.current) return; // already in a call — ignore (server also busy-guards)
      // Mini banner heuristic: unscheduled AND (typing OR on the chat page).
      const el = typeof document !== "undefined" ? document.activeElement : null;
      const midInput = !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
      const preferMini = !msg.meeting_title && (midInput || location.pathname === "/chat");
      setIncoming({
        caller_name:  msg.caller_name || "Someone",
        caller_id:    msg.caller_id || "",
        room_id:      msg.room_id || "",
        audio_only:   !!msg.audio_only,
        participants: msg.participants || [],
        meeting_title: msg.meeting_title || null,
        preferMini,
      });
    },
    onCallCancelled: (msg) => {
      // Caller aborted or timed out. If we had a live incoming card, dismiss it
      // and surface a missed toast (the thread also gets a missed entry from the
      // CallLog). The 30s timeout itself is caller-side; this is the callee cue.
      const c = incomingRef.current;
      if (c && (!msg.room_id || c.room_id === msg.room_id)) {
        setIncoming(null);
        toast?.info?.(`Missed call from ${c.caller_name || "someone"}`);
      }
    },
    onCallDeclined:   ()    => showDeclined(null),
    onCallQuickReply: (msg) => showDeclined(msg?.text || null),
    onRecordingNotice: (msg) => {
      setRecordingNotice({ message: msg.message, action: msg.action });
      if (msg.action === "stopped") setTimeout(() => setRecordingNotice(null), 4000);
    },
    onUserFree: (msg) => { toast?.info?.(`${msg?.user_name || "They"} is free now — call again?`); },
  });

  const value = { startCall, joinRoom, acceptCall, acceptAudio, declineCall, quickReply, closeCall, callOpen };

  return (
    <CallContext.Provider value={value}>
      {children}

      {active && (
        <>
          {/* Incoming call — full card / mini banner + quick replies (z 10085) */}
          <IncomingCallToast
            call={incomingCall}
            preferMini={incomingCall?.preferMini}
            onAccept={acceptCall}
            onAcceptAudio={acceptAudio}
            onDecline={declineCall}
            onQuickReply={quickReply}
          />

          {/* Outgoing / busy status card (z 10082) */}
          <CallOutgoingCard
            outgoing={outgoing}
            onCancel={cancelCall}
            onDismiss={dismissOutgoing}
            onRetry={retryOutgoing}
            onLeaveMessage={leaveMessage}
            onPingWhenFree={pingWhenFree}
          />

          {/* Recording consent notice (in-call, z 10088) */}
          {recordingNotice && (
            <div style={{
              position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
              zIndex: 10088, background: "rgba(5,5,5,0.98)", border: "1px solid rgba(239,68,68,0.5)",
              borderRadius: 20, padding: "28px 32px", minWidth: 400, maxWidth: 480,
              boxShadow: "0 16px 60px rgba(239,68,68,0.25), 0 0 0 1px rgba(239,68,68,0.1)",
            }}>
              {recordingNotice.action !== "stopped" ? (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 20, background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.4)" }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#ef4444", display: "block", animation: "recPulse 1s ease-in-out infinite" }} />
                      <span style={{ color: "#ef4444", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em" }}>LIVE RECORDING</span>
                    </div>
                  </div>
                  <p style={{ color: "#fff", fontSize: 17, fontWeight: 700, marginBottom: 8 }}>This call is being recorded</p>
                  <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, lineHeight: 1.6, marginBottom: 24 }}>{recordingNotice.message}</p>
                  <div style={{ display: "flex", gap: 10 }}>
                    <button onClick={() => setRecordingNotice(null)}
                      style={{ flex: 1, padding: "11px 0", borderRadius: 100, background: "#00FF99", border: "none", color: "#000", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                      OK, I understand
                    </button>
                    <button onClick={() => { setRecordingNotice(null); closeCall(0); }}
                      style={{ flex: 1, padding: "11px 0", borderRadius: 100, background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.4)", color: "#f87171", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                      Leave Call
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: "#ef4444", flexShrink: 0 }} />
                  <div>
                    <p style={{ color: "#fff", fontSize: 14, fontWeight: 600, marginBottom: 2 }}>Recording stopped</p>
                    <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 12 }}>The recording will be shared in chat shortly.</p>
                  </div>
                </div>
              )}
            </div>
          )}
          <style>{`@keyframes recPulse { 0%,100%{opacity:1;} 50%{opacity:0.3;} }`}</style>

          {/* The single call modal for the whole app (z 10080) */}
          <VideoCallModal
            open={callOpen}
            roomId={callConfig?.room_id || null}
            targetUser={callConfig?.targetUser || null}
            audioOnly={callConfig?.audioOnly || false}
            onClose={(secs) => closeCall(secs)}
            onAnswered={onAnswered}
          />
        </>
      )}
    </CallContext.Provider>
  );
}
